import { useEffect, useState } from 'react'
import { User as UserIcon } from 'lucide-react'
import { type User } from '@/store/authSessionSlice'
import { USER_AVATAR_GRADIENT_CLASS } from '@/utils/userUtils'
import { cn } from '@/lib/utils'

type ProfilePictureProps = {
  user: User | null,
  /** Pixel size. String accepted because the existing call sites pass one. */
  size?: string | number,
  clickable?: boolean,
}

const DEFAULT_SIZE_PX = 48

const ProfilePicture = ({ user, size, clickable = true }: ProfilePictureProps) => {
  /**
   * A photo URL can stop resolving while this component is mounted — most
   * commonly because someone removed the photo on their Google account, which
   * leaves a dead URL in the `picture` claim (see UI-04: OrgSyncInterceptor
   * rewrites `organization_members.picture` from that claim). With no handler
   * the browser paints its own broken-image placeholder; Safari's is a question
   * mark, which is what users were seeing in chat.
   *
   * Reset on `user` rather than on the URL alone: authSessionSlice rebuilds the
   * object on every auth refresh, so that is the moment even the SAME url
   * deserves another attempt. Same guard as UserMenu and CommentBubble.
   */
  const [failed, setFailed] = useState(false)
  const source = user?.picture
  // Deps match CommentBubble's guard deliberately: keying on the source as
  // well as `user` means this does not rely on authSessionSlice replacing the
  // object rather than mutating `picture` in place — an invariant that holds
  // today but which a reader would otherwise have to go and confirm.
  useEffect(() => {
    setFailed(false)
  }, [source, user])

  const src = failed ? null : source
  const initial =
    user?.given_name?.charAt(0)?.toUpperCase() ||
    user?.name?.charAt(0)?.toUpperCase() ||
    null

  // Sized inline, not with `w-[${size}px]`: an interpolated class is invisible
  // to Tailwind's scanner, so the old markup only rendered at 32px because an
  // unrelated file happened to contain that literal.
  // parseFloat, not Number: a caller passing a CSS-ish "40px" would make
  // Number() return NaN and silently collapse to the default.
  const parsed = typeof size === 'number' ? size : parseFloat(size ?? '')
  const px = Number.isFinite(parsed) && parsed > 0 ? parsed : DEFAULT_SIZE_PX

  return (
    <figure
      className={cn(
        'flex items-center justify-center overflow-hidden rounded-full font-medium text-white',
        USER_AVATAR_GRADIENT_CLASS,
        // The gradient replaced a gray circle, so a hover BACKGROUND change is
        // no longer visible. Keep a hover affordance that works on a gradient
        // and in both themes.
        clickable && 'cursor-pointer transition-opacity hover:opacity-90'
      )}
      // 0.4 keeps a single initial optically centred at every size we use
      // (32-48px) — the same ratio CommentUserAvatar and AvatarStack derive
      // their font size from, so the three read as one component.
      style={{ width: px, height: px, fontSize: Math.round(px * 0.4) }}
    >
      {src ? (
        // The gradient stays underneath as the backdrop while the photo loads,
        // so the circle never flashes empty.
        <img
          src={src}
          alt=""
          className="size-full object-cover"
          referrerPolicy="no-referrer"
          draggable={false}
          onError={() => setFailed(true)}
        />
      ) : initial ? (
        initial
      ) : (
        <UserIcon
          aria-hidden
          style={{ width: Math.round(px * 0.5), height: Math.round(px * 0.5) }}
        />
      )}
    </figure>
  )
}

export default ProfilePicture
